/**
 * FP3-130 — Print Progress Summary dialog.
 *
 * Opened from the Progress page toolbar. Two knobs only:
 *   - Students: reflects current selection (or "All current students"
 *     when nothing is selected)
 *   - Date range: default "Current School Year to Date" (backend
 *     resolves start = active schedule_period.start_date, end =
 *     today) OR a Custom range with two date inputs.
 *
 * Primary action opens a NEW TAB pointing at
 *   /print/progress-summary?student_ids=<csv>&start=<iso>&end=<iso>
 * The report page itself uses `window.print()` from the PrintShell
 * toolbar for browser Save-as-PDF.
 */
import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer } from "@phosphor-icons/react";

// Local YYYY-MM-DD without any TZ shift. Preferred over
// `.toISOString().slice(0,10)` which shifts west after midnight.
function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function PrintProgressReportDialog({
  open,
  onOpenChange,
  selectedIds,
  totalStudents,
  hasSupervisedAssistants = false,
}) {
  const [range, setRange] = useState("school_year"); // "school_year" | "custom"
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState(todayISO());
  // FP3-130A — Student scope. Only relevant when zero students are
  // manually selected AND the caller supervises assistants (both
  // conditions gate the radio group below). Default: "direct".
  const [studentScope, setStudentScope] = useState("direct");

  const selectionCount = selectedIds?.length || 0;
  const showScopeRadio = selectionCount === 0 && hasSupervisedAssistants;
  const studentSummary = useMemo(() => {
    if (selectionCount === 0) {
      if (showScopeRadio) {
        return studentScope === "direct"
          ? "My direct students"
          : `All my current students (${totalStudents || 0})`;
      }
      return `All current students (${totalStudents || 0})`;
    }
    if (selectionCount === 1) return "1 selected student";
    return `${selectionCount} selected students`;
  }, [selectionCount, totalStudents, showScopeRadio, studentScope]);

  const handlePrint = () => {
    const params = new URLSearchParams();
    if (selectionCount > 0) {
      params.set("student_ids", selectedIds.join(","));
    } else if (showScopeRadio) {
      // Manual selection ALWAYS wins over scope; scope only rides
      // along when nothing is ticked.
      params.set("scope", studentScope);
    }
    if (range === "custom") {
      if (customStart) params.set("start", customStart);
      if (customEnd) params.set("end", customEnd);
    }
    const q = params.toString();
    const url = `/print/progress-summary${q ? `?${q}` : ""}`;
    // New tab preserves the caller's Progress page for iteration.
    window.open(url, "_blank", "noopener,noreferrer");
    onOpenChange?.(false);
  };

  const customRangeInvalid =
    range === "custom" &&
    ((!customStart || !customEnd) ||
      (customStart && customEnd && customStart > customEnd));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="print-progress-dialog"
      >
        <DialogHeader>
          <DialogTitle>Print Progress Summary</DialogTitle>
          <DialogDescription>
            A concise printable summary of current active goals and
            attended sessions. Opens in a new tab where you can Print
            or Save as PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <Label className="text-[11px] uppercase tracking-wide text-[#6B7280] font-medium">
              Students
            </Label>
            <p
              className="mt-1 text-sm text-[#1F2937]"
              data-testid="print-dialog-student-summary"
            >
              {studentSummary}
            </p>
          </div>

          {showScopeRadio && (
            <div data-testid="student-scope-group">
              <Label className="text-[11px] uppercase tracking-wide text-[#6B7280] font-medium">
                Student scope
              </Label>
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="fp3130a-scope"
                    value="direct"
                    checked={studentScope === "direct"}
                    onChange={() => setStudentScope("direct")}
                    data-testid="scope-direct"
                  />
                  My direct students
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="radio"
                    name="fp3130a-scope"
                    value="all"
                    checked={studentScope === "all"}
                    onChange={() => setStudentScope("all")}
                    data-testid="scope-all"
                  />
                  All my current students
                </label>
              </div>
              <p className="mt-1.5 text-[11px] text-[#6B7280]">
                Students you serve directly, or your full caseload
                including those assigned to your assistants.
              </p>
            </div>
          )}

          <div>
            <Label className="text-[11px] uppercase tracking-wide text-[#6B7280] font-medium">
              Date range
            </Label>
            <div className="mt-2 space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="fp3130-range"
                  value="school_year"
                  checked={range === "school_year"}
                  onChange={() => setRange("school_year")}
                  data-testid="range-school-year"
                />
                Current School Year to Date
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="fp3130-range"
                  value="custom"
                  checked={range === "custom"}
                  onChange={() => setRange("custom")}
                  data-testid="range-custom"
                />
                Custom Date Range
              </label>
              {range === "custom" && (
                <div
                  className="pl-6 pt-1 grid grid-cols-2 gap-2"
                  data-testid="custom-range-fields"
                >
                  <div>
                    <Label
                      className="text-[10px] text-[#6B7280]"
                      htmlFor="fp3130-start"
                    >
                      Start
                    </Label>
                    <Input
                      id="fp3130-start"
                      type="date"
                      value={customStart}
                      onChange={(e) => setCustomStart(e.target.value)}
                      className="h-9"
                      data-testid="print-dialog-start"
                    />
                  </div>
                  <div>
                    <Label
                      className="text-[10px] text-[#6B7280]"
                      htmlFor="fp3130-end"
                    >
                      End
                    </Label>
                    <Input
                      id="fp3130-end"
                      type="date"
                      value={customEnd}
                      onChange={(e) => setCustomEnd(e.target.value)}
                      className="h-9"
                      data-testid="print-dialog-end"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="print-dialog-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrint}
            disabled={customRangeInvalid}
            data-testid="print-dialog-print"
            className="bg-[#1E3F33] hover:bg-[#2A5544] text-white"
          >
            <Printer size={14} weight="bold" className="mr-1.5" />
            Print / Save PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
